import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Container, Row, Col, Card, Button, Form, Alert, Spinner, Modal, Image } from 'react-bootstrap';
import { Share } from 'react-bootstrap-icons';
import axios from '../utils/api';
import { useCart } from '../contexts/CartContext';
import { useAuth, ROLES } from '../contexts/AuthContext';
import { useStoreSettings } from '../contexts/StoreSettingsContext';
import { parseProductSelectionState, resolveInitialVariant } from '../utils/variantSelection';
import InnerImageZoom from 'react-inner-image-zoom';
// import 'react-inner-image-zoom/lib/InnerImageZoom/styles.css';
import 'react-inner-image-zoom/lib/styles.min.css'

const ProductDetail = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, hasRole } = useAuth();
  const { addItem } = useCart();
  const { settings } = useStoreSettings();
  const isLoggedIn = Boolean(user);
  const canAddToCart = hasRole(ROLES.BUYER);
  const addToCartEnabled = settings?.allow_add_to_cart !== false;
  const reviewQuery = new URLSearchParams(location.search).get('review');
  const selectionState = useMemo(() => parseProductSelectionState(location.search), [location.search]);

  const [product, setProduct] = useState(null);
  const [productColors, setProductColors] = useState([]);
  const [selectedImageUrl, setSelectedImageUrl] = useState('');
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [selectedColor, setSelectedColor] = useState('');
  const [selectedSize, setSelectedSize] = useState('');
  const [showImageModal, setShowImageModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(true);
  const [reviews, setReviews] = useState([]);
  const [reviewStats, setReviewStats] = useState({ average_rating: 0, review_count: 0 });
  const [canReview, setCanReview] = useState(false);
  const [reviewLoading, setReviewLoading] = useState(true);
  const [reviewError, setReviewError] = useState('');
  const [reviewSuccess, setReviewSuccess] = useState('');
  const [ratingInput, setRatingInput] = useState(5);
  const [commentInput, setCommentInput] = useState('');
  const [shareForm, setShareForm] = useState({
    name: '',
    email: '',
    phone_number: '',
  });
  const [sharing, setSharing] = useState(false);
  const [shareError, setShareError] = useState('');
  const [shareSuccess, setShareSuccess] = useState('');

  const variantsForSelection = useMemo(() => {
    const allVariants = Array.isArray(product?.variants) ? product.variants : [];
    if (!canAddToCart) {
      return allVariants;
    }

    return allVariants.filter((variant) => variant?.is_active !== false && variant?.sold_out === false);
  }, [product, canAddToCart]);

  const normalizeText = (value) => (typeof value === 'string' ? value.trim() : '');

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);
        const [productResponse, colorsResponse] = await Promise.all([
          axios.get(`/api/products/${productId}/with-variants`),
          axios.get(`/api/products/${productId}/colors-with-urls`)
        ]);

        const productData = productResponse.data;
        const colorsData = Array.isArray(colorsResponse.data) ? colorsResponse.data : [];

        setProduct(productData);
        setProductColors(colorsData);

        // Record product view (fire-and-forget, do not block page load)
        axios.post('/api/product-views', { product_id: productId }).catch(() => {});

        if (productData.has_variants && productData.variants.length > 0) {
          const initialSelection = resolveInitialVariant({
            variants: productData.variants,
            selectionState,
            canAddToCart,
          });

          if (!initialSelection.selectedVariant) {
            setSelectedVariant(null);
            setSelectedColor('');
            setSelectedSize('');
            setSelectedImageUrl('');
            return;
          }

          const selectedColorId = initialSelection.selectedColor || initialSelection.selectedVariant.color_id || '';
          const selectedSizeId = initialSelection.selectedSize || initialSelection.selectedVariant.size_id || '';
          const initialColor = colorsData.find((c) => c.color_id === selectedColorId) || colorsData.find((c) => c.is_default) || colorsData[0];
          const initialImage = initialColor?.images?.find((img) => img.is_default) || initialColor?.images?.[0] || null;

          setSelectedVariant(initialSelection.selectedVariant);
          setSelectedColor(selectedColorId);
          setSelectedSize(selectedSizeId);
          setSelectedImageUrl(initialSelection.selectedImageUrl || initialImage?.small_url || initialImage?.thumbnail_url || '');
        }
      } catch (err) {
        setError('Failed to load product details');
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [productId, canAddToCart, selectionState]);

  const fetchReviews = async () => {
    try {
      setReviewLoading(true);
      setReviewError('');
      const [reviewsRes, statsRes] = await Promise.all([
        axios.get(`/api/reviews/product/${productId}?published_only=true`),
        axios.get(`/api/reviews/product/${productId}/stats`),
      ]);

      setReviews(Array.isArray(reviewsRes.data) ? reviewsRes.data : []);
      setReviewStats({
        average_rating: Number(statsRes.data?.average_rating || 0),
        review_count: Number(statsRes.data?.review_count || 0),
      });
    } catch {
      setReviewError('Failed to load product reviews');
    } finally {
      setReviewLoading(false);
    }
  };

  useEffect(() => {
    fetchReviews();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]);

  useEffect(() => {
    const fetchReviewEligibility = async () => {
      if (!canAddToCart) {
        setCanReview(false);
        return;
      }
      try {
        const response = await axios.get(`/api/reviews/can-review/${productId}`);
        setCanReview(Boolean(response.data?.can_review));
      } catch {
        setCanReview(false);
      }
    };

    fetchReviewEligibility();
  }, [productId, canAddToCart]);

  useEffect(() => {
    if (!product || !product.has_variants) {
      setSelectedVariant(null);
      return;
    }

    const variant = variantsForSelection.find(v =>
      (selectedColor ? v.color_id === selectedColor : true) &&
      (selectedSize ? v.size_id === selectedSize : true)
    );

    setSelectedVariant(variant || null);
  }, [selectedColor, selectedSize, product, variantsForSelection]);

  const availableColors = useMemo(() => {
    const allowedColorIds = new Set(
      variantsForSelection
        .map((variant) => variant?.color_id)
        .filter(Boolean)
    );

    if (productColors.length > 0) {
      if (!canAddToCart) {
        return productColors;
      }
      return productColors.filter((item) => allowedColorIds.has(item.color_id));
    }

    const map = new Map();
    variantsForSelection.forEach((variant) => {
      if (variant.color?.id && !map.has(variant.color.id)) {
        map.set(variant.color.id, {
          color_id: variant.color.id,
          color: variant.color,
          images: []
        });
      }
    });
    return Array.from(map.values());
  }, [productColors, variantsForSelection, canAddToCart]);

  const selectedColorData = useMemo(
    () => availableColors.find((colorItem) => colorItem.color_id === selectedColor) || null,
    [availableColors, selectedColor]
  );

  const selectedColorImages = useMemo(
    () => selectedColorData?.images || [],
    [selectedColorData]
  );

  useEffect(() => {
    if (!selectedColorData) {
      setSelectedImageUrl('');
      return;
    }

    const requestedImageUrl = selectionState?.imageUrl || '';
    const requestedImageMatchesCurrentColor = selectedColorImages.some((img) => {
      const candidates = [img.small_url, img.thumbnail_url, img.large_url].filter(Boolean);
      return candidates.includes(requestedImageUrl);
    });

    if (requestedImageMatchesCurrentColor && requestedImageUrl) {
      setSelectedImageUrl(requestedImageUrl);
      return;
    }

    const defaultImage = selectedColorImages.find((img) => img.is_default) || selectedColorImages[0] || null;
    const nextImageUrl = defaultImage?.small_url || defaultImage?.thumbnail_url || '';
    setSelectedImageUrl(nextImageUrl);
  }, [selectedColorData, selectedColorImages, selectionState?.imageUrl]);

  const getColorHex = (colorObj) => {
    if (!colorObj) return '#adb5bd';
    const candidate = colorObj.hex_code || colorObj.hex || colorObj.code;
    return typeof candidate === 'string' && candidate.trim() ? candidate : '#adb5bd';
  };

  const formatMoney = (value) => {
    const numberValue = Number(value);
    if (!Number.isFinite(numberValue)) return 'NA';
    return `Rs. ${numberValue.toFixed(2)}`;
  };

  const formatReviewDate = (value) => {
    if (!value) return '-';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return value;
    return new Intl.DateTimeFormat('en-IN', {
      timeZone: 'Asia/Kolkata',
      dateStyle: 'medium',
      timeStyle: 'short',
      hour12: true,
    }).format(parsed);
  };

  const renderStars = (ratingValue) => {
    const safe = Math.max(0, Math.min(5, Number(ratingValue || 0)));
    const rounded = Math.round(safe);
    return `${'★'.repeat(rounded)}${'☆'.repeat(5 - rounded)}`;
  };

  const displayedStock = selectedVariant?.current_quantity != null
    ? selectedVariant.current_quantity
    : product?.stock_quantity;
  const addToCartDisabled = isLoggedIn
    ? (!canAddToCart || !addToCartEnabled || !displayedStock || displayedStock < 1)
    : false;

  const cartMessages = useMemo(() => {
    if (Array.isArray(settings?.cart_messages) && settings.cart_messages.length > 0) {
      return settings.cart_messages;
    }
    return [
      'Free shipping on orders over $50',
      '30-day return policy',
      'Secure checkout',
    ];
  }, [settings]);

  const openImageModal = () => {
    if (!selectedImageUrl) return;
    setShowImageModal(true);
  };

  const selectedModalImageUrl = useMemo(() => {
    const selectedImage = selectedColorImages.find(
      (img) => (img.small_url || img.thumbnail_url || '') === selectedImageUrl
    );
    return selectedImage?.large_url || selectedImage?.small_url || selectedImage?.thumbnail_url || selectedImageUrl;
  }, [selectedColorImages, selectedImageUrl]);

  useEffect(() => {
    if (reviewQuery !== '1') return;
    const timer = setTimeout(() => {
      const section = document.getElementById('product-reviews-section');
      if (section) {
        section.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 150);

    return () => clearTimeout(timer);
  }, [reviewQuery]);

  const handleAddToCart = async () => {
    if (!product) return;

    if (!isLoggedIn) {
      navigate('/login');
      return;
    }

    if (!canAddToCart) {
      setError('Only Buyer role can add products to cart.');
      setSuccess('');
      return;
    }

    if (!addToCartEnabled) {
      setError('Currently add to cart is disabled.');
      setSuccess('');
      return;
    }

    const availableStock = selectedVariant
      ? (selectedVariant.current_quantity ?? selectedVariant.stock_quantity)
      : product.stock_quantity;
    if (!availableStock || quantity < 1) {
      setError('Invalid quantity or out of stock');
      setSuccess('');
      return;
    }

    if (quantity > availableStock) {
      setError('Quantity exceeds available stock');
      setSuccess('');
      return;
    }

    const addResult = await addItem(product, selectedVariant, quantity);
    if (!addResult?.success) {
      setError(addResult?.error || 'Could not add item to cart.');
      setSuccess('');
      return;
    }

    setError('');
    setSuccess('Added to cart successfully!');
  };

  const handleSubmitReview = async (event) => {
    event.preventDefault();
    setReviewError('');
    setReviewSuccess('');

    try {
      await axios.post('/api/reviews', {
        product_id: productId,
        rating: Number(ratingInput),
        comment: commentInput?.trim() || null,
      });
      setReviewSuccess('Your review has been saved successfully.');
      await fetchReviews();
    } catch (err) {
      setReviewError(err.response?.data?.detail || 'Failed to save review');
    }
  };

  const handleShareInputChange = (event) => {
    const { name, value, type, checked } = event.target;
    setShareForm((current) => ({
      ...current,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const resetShareForm = () => {
    setShareForm({
      name: '',
      email: '',
      phone_number: '',
    });
  };

  const handleSubmitShareProduct = async (event) => {
    event.preventDefault();
    setShareError('');
    setShareSuccess('');

    const name = normalizeText(shareForm.name);
    const email = normalizeText(shareForm.email);
    const phoneNumber = normalizeText(shareForm.phone_number);

    if (!name) {
      setShareError('Name is required');
      return;
    }

    if (!email && !phoneNumber) {
      setShareError('Please enter at least one contact field: Email or Phone Number.');
      return;
    }

    try {
      setSharing(true);
      await axios.post('/api/friend-invites/share-product', {
        friend_name: name,
        friend_email: email || null,
        friend_phone_number: phoneNumber || null,
        message_type: 'Share product',
        product_id: productId,
      });

      setShareSuccess('Successfully shared the product.');
      resetShareForm();
      setShowShareModal(false);
    } catch (shareRequestError) {
      setShareError(shareRequestError.response?.data?.detail || 'Failed to capture product share details');
    } finally {
      setSharing(false);
    }
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading product details...</span>
        </Spinner>
        <p className="mt-2">Loading product details...</p>
      </Container>
    );
  }

  if (!product) {
    return (
      <Container className="py-5">
        <Alert variant="danger">
          Failed to load product details.
        </Alert>
      </Container>
    );
  }

  return (
    <Container className="py-4">
      <Row className="mb-3">
        <Col>
          <Button variant="outline-secondary" onClick={() => navigate(-1)}>
            ← Back to Products
          </Button>
        </Col>
      </Row>

      {error && (
        <Row className="mb-3">
          <Col>
            <Alert variant="danger" dismissible onClose={() => setError('')}>
              {error}
            </Alert>
          </Col>
        </Row>
      )}

      {success && (
        <Row className="mb-3">
          <Col>
            <Alert variant="success" dismissible onClose={() => setSuccess('')}>
              {success}
            </Alert>
          </Col>
        </Row>
      )}

      {shareSuccess && (
        <Row className="mb-3">
          <Col>
            <Alert variant="success" dismissible onClose={() => setShareSuccess('')}>
              {shareSuccess}
            </Alert>
          </Col>
        </Row>
      )}

      {shareError && (
        <Row className="mb-3">
          <Col>
            <Alert variant="danger" dismissible onClose={() => setShareError('')}>
              {shareError}
            </Alert>
          </Col>
        </Row>
      )}

      <Row>
        <Col lg={8}>
          <Card className="mb-4">
            <Card.Body>
              <Row className="g-4">
                {product.has_variants && (
                  <Col md={5}>
                    <Card className="h-100 border">
                      <Card.Header>
                        <h5 className="mb-0">Choose Variant</h5>
                      </Card.Header>
                      <Card.Body>
                        <Row>
                          <Col xs={12} className="mb-3">
                            <Form.Label>Color Image</Form.Label>
                            {selectedImageUrl ? (
                              <div className="border rounded p-2 bg-light">
                                <Image src={selectedImageUrl}
                                  alt={selectedColorData?.color?.name || 'Selected color'}
                                  onClick={openImageModal}
                                  style={{ width: '100%', objectFit: 'cover', borderRadius: '6px', cursor: 'zoom-in' }}
                                  fluid />
                                {/* <img
                                  src={selectedImageUrl}
                                  alt={selectedColorData?.color?.name || 'Selected color'}
                                  onClick={openImageModal}
                                  style={{ width: '100%', objectFit: 'cover', borderRadius: '6px', cursor: 'zoom-in' }}
                                /> */}
                              </div>
                            ) : (
                              <div className="border rounded p-3 text-muted text-center bg-light">
                                No image for selected color
                              </div>
                            )}
                          </Col>

                          <Col xs={12}>
                            <Form.Label>Thumbnails</Form.Label>
                            <div className="d-flex gap-2 flex-wrap">
                              {selectedColorImages.length > 0 ? selectedColorImages.map((img) => {
                                const thumbUrl = img.thumbnail_url || img.small_url;
                                const smallUrl = img.small_url || img.thumbnail_url;
                                const isSelected = selectedImageUrl === smallUrl;

                                return (
                                  <img
                                    key={img.id}
                                    src={thumbUrl}
                                    alt={img.image_name || 'Thumbnail'}
                                    onClick={() => setSelectedImageUrl(smallUrl || '')}
                                    style={{
                                      width: '52px',
                                      height: '52px',
                                      objectFit: 'cover',
                                      borderRadius: '6px',
                                      border: isSelected ? '2px solid #0d6efd' : '1px solid #ced4da',
                                      cursor: 'pointer'
                                    }}
                                  />
                                );
                              }) : (
                                <span className="text-muted">No thumbnails available</span>
                              )}
                            </div>
                          </Col>

                          <Col xs={12} className="mb-3">
                            <Form.Group>
                              <Form.Label>Available Colors</Form.Label>
                              <div className="d-flex flex-wrap gap-2">
                                {availableColors.map((colorItem) => {
                                  const isSelected = selectedColor === colorItem.color_id;
                                  return (
                                    <button
                                      key={colorItem.color_id}
                                      type="button"
                                      title={colorItem.color?.name || 'Color'}
                                      onClick={() => setSelectedColor(colorItem.color_id)}
                                      style={{
                                        width: '30px',
                                        height: '30px',
                                        borderRadius: '6px',
                                        border: isSelected ? '2px solid #0d6efd' : '1px solid #adb5bd',
                                        backgroundColor: '#ffffff',
                                        cursor: 'pointer',
                                        padding: 0,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                      }}
                                    >
                                      <span
                                        style={{
                                          width: '18px',
                                          height: '18px',
                                          borderRadius: '50%',
                                          border: '1px solid #ced4da',
                                          backgroundColor: getColorHex(colorItem.color),
                                          display: 'inline-block'
                                        }}
                                      />
                                    </button>
                                  );
                                })}
                              </div>
                            </Form.Group>
                          </Col>

                          <Col xs={12} className="mb-3">
                            <Form.Group>
                              <Form.Label>Size</Form.Label>
                              <Form.Select
                                value={selectedSize}
                                onChange={(e) => setSelectedSize(e.target.value)}
                              >
                                <option value="">Any Size</option>
                                {variantsForSelection
                                  .map(v => v.size)
                                  .filter((v, i, arr) => v && arr.findIndex(x => x?.id === v.id) === i)
                                  .map(size => (
                                    <option key={size.id} value={size.id}>
                                      {size.name} ({size.value} {size.unit})
                                    </option>
                                  ))}
                              </Form.Select>
                            </Form.Group>
                          </Col>

                          {selectedVariant && (
                            <Col xs={12} className="mb-3">
                              <Form.Label>Variant Price</Form.Label>
                              <div className="border rounded p-2 bg-light">
                                <div className="text-muted" style={{ textDecoration: 'line-through' }}>
                                  {formatMoney(selectedVariant.final_price_per_unit)}
                                </div>
                                <div className="fw-bold" style={{ fontSize: '1rem' }}>
                                  {formatMoney(selectedVariant.final_sp_unit_discount)}
                                </div>
                                <div className="mt-2">
                                  <span className="text-muted me-2">Available Stock:</span>
                                  <span className="fw-semibold">{selectedVariant.current_quantity ?? selectedVariant.stock_quantity ?? 0}</span>
                                </div>
                              </div>
                            </Col>
                          )}
                        </Row>
                        <Row>
                          <Col>
                        {selectedVariant && (
                          <Alert variant="info" style={{ display: 'block' }}>
                            <p className="mb-1"><strong>Selected:</strong></p>
                            {selectedVariant.color?.name && (
                              <p className="mb-1">Color: {selectedVariant.color.name}</p>
                            )}
                            {selectedVariant.size && (
                              <p className="mb-0">Size: {selectedVariant.size.name}{selectedVariant.size.value ? ` - ${selectedVariant.size.value}${selectedVariant.size.unit ? selectedVariant.size.unit : ''}` : ''}</p>
                            )}
                          </Alert>
                        )}                          
                          </Col>
                        </Row>

                      </Card.Body>
                    </Card>
                  </Col>
                )}

                <Col md={product.has_variants ? 7 : 12}>
                  <Card.Title as="h1" className="mb-3">{product.name}</Card.Title>
                  <div
                    className="lead mb-4"
                    dangerouslySetInnerHTML={{ __html: product.description || '' }}
                  />
                  {product.material && (
                    <div className="mb-2 small">
                      <span className="text-muted fw-semibold">Material: </span>
                      <span>{product.material}</span>
                    </div>
                  )}
                  <div className="mb-2 small">
                    <span className="text-muted fw-semibold">Return Policy: </span>
                    <span>{
                      (() => {
                        const policy = product.return_policy_override || settings.default_return_policy || 'non-returnable';
                        const labels = {
                          'non-returnable': 'Non-Returnable',
                          '5-days': '5 Days',
                          '10-days': '10 Days',
                          '15-days': '15 Days',
                          '30-days': '30 Days',
                        };
                        return labels[policy] || policy;
                      })()
                    }</span>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={4}>
          <Card className="sticky-top" style={{ top: '20px' }}>
            <Card.Body>
              <h5>Add to Cart</h5>

              {!canAddToCart && (
                <Alert variant="warning" className="mb-3">
                  {!isLoggedIn
                    ? 'Please log in to perform actions on this page.'
                    : 'View-only mode for this role. Cart is available only for Buyer.'}
                </Alert>
              )}

              {canAddToCart && !addToCartEnabled && (
                <Alert variant="info" className="mb-3">
                  Currently add to cart is disabled. You will be able to buy from site soon...
                </Alert>
              )}

              <Form.Group className="mb-3">
                <Form.Label>Quantity</Form.Label>
                <Form.Control
                  type="number"
                  min="1"
                  max={displayedStock || 999}
                  value={quantity}
                  onChange={(e) => setQuantity(Math.max(1, Number(e.target.value)))}
                  disabled={!canAddToCart || !addToCartEnabled || !displayedStock || displayedStock < 1}
                />
              </Form.Group>

              <div className="d-grid">
                <Button
                  variant="primary"
                  size="lg"
                  onClick={handleAddToCart}
                  disabled={addToCartDisabled}
                >
                  {!canAddToCart
                    ? (!isLoggedIn ? 'Log in to Add to cart' : 'Not Available for This Role')
                    : (!addToCartEnabled
                        ? 'Add to Cart Disabled'
                        : (displayedStock > 0 ? 'Add to Cart' : 'Out of Stock'))}
                </Button>
              </div>

              {hasRole(ROLES.BUYER) && (
                <div className="d-grid mt-2">
                  <Button
                    variant="link"
                    className="text-success text-decoration-none px-0 text-start"
                    onClick={() => setShowShareModal(true)}
                  >
                    <Share className="me-1" />
                    Share with others
                  </Button>
                </div>
              )}

              <div className="mt-3 text-muted small">
                <ul className="mb-0 ps-3">
                  {cartMessages.map((message, index) => (
                    <li key={`${index}-${message}`}>{message}</li>
                  ))}
                </ul>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="mt-2" id="product-reviews-section">
        <Col>
          <Card>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                <h4 className="mb-0">Product Reviews</h4>
                <div className="small text-muted">
                  <span className="text-warning">{renderStars(reviewStats.average_rating)}</span>
                  <span className="ms-2 fw-semibold text-dark">{reviewStats.average_rating.toFixed(1)}</span>
                  <span className="ms-1">({reviewStats.review_count} reviews)</span>
                </div>
              </div>

              {reviewError && <Alert variant="danger" dismissible onClose={() => setReviewError('')}>{reviewError}</Alert>}
              {reviewSuccess && <Alert variant="success" dismissible onClose={() => setReviewSuccess('')}>{reviewSuccess}</Alert>}

              {hasRole(ROLES.BUYER) && (
                <Card className="mb-4 border">
                  <Card.Body>
                    <h6 className="mb-3">Write a Review</h6>
                    {canReview ? (
                      <Form onSubmit={handleSubmitReview}>
                        <Row className="g-3">
                          <Col md={3}>
                            <Form.Group>
                              <Form.Label>Rating</Form.Label>
                              <Form.Select value={ratingInput} onChange={(e) => setRatingInput(Number(e.target.value))}>
                                <option value={5}>5 - Excellent</option>
                                <option value={4}>4 - Very Good</option>
                                <option value={3}>3 - Good</option>
                                <option value={2}>2 - Fair</option>
                                <option value={1}>1 - Poor</option>
                              </Form.Select>
                            </Form.Group>
                          </Col>
                          <Col md={9}>
                            <Form.Group>
                              <Form.Label>Comments</Form.Label>
                              <Form.Control
                                as="textarea"
                                rows={3}
                                value={commentInput}
                                onChange={(e) => setCommentInput(e.target.value)}
                                placeholder="Share your feedback about this product"
                              />
                            </Form.Group>
                          </Col>
                          <Col xs={12}>
                            <Button type="submit" variant="primary">Save Review</Button>
                          </Col>
                        </Row>
                      </Form>
                    ) : (
                      <Alert variant="warning" className="mb-0">
                        Review submission is available only for products you have purchased.
                      </Alert>
                    )}
                  </Card.Body>
                </Card>
              )}

              {reviewLoading ? (
                <div className="text-center py-3"><Spinner animation="border" size="sm" /></div>
              ) : reviews.length === 0 ? (
                <Alert variant="info" className="mb-0">No published reviews for this product yet.</Alert>
              ) : (
                <div className="d-flex flex-column gap-3">
                  {reviews.map((review) => (
                    <Card key={review.id} className="border">
                      <Card.Body>
                        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
                          <div>
                            <strong>{review.user_name || 'Buyer'}</strong>
                            <div className="small text-muted">{formatReviewDate(review.created_at)}</div>
                          </div>
                          <div className="text-warning fw-semibold">{renderStars(review.rating)}</div>
                        </div>
                        <div className="text-dark">{review.comment || 'No comment provided.'}</div>
                        {review.admin_reply && (
                          <Alert variant="light" className="mt-3 mb-0 border">
                            <div className="fw-semibold mb-1">Admin Reply</div>
                            <div>{review.admin_reply}</div>
                            <div className="small text-muted mt-1">
                              {review.admin_reply_by_name ? `By ${review.admin_reply_by_name}` : 'By Admin'}
                              {review.admin_reply_at ? ` on ${formatReviewDate(review.admin_reply_at)}` : ''}
                            </div>
                          </Alert>
                        )}
                      </Card.Body>
                    </Card>
                  ))}
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Modal
        show={showShareModal}
        onHide={() => setShowShareModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Share Product</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmitShareProduct}>
            <Form.Group className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={shareForm.name}
                onChange={handleShareInputChange}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                name="email"
                value={shareForm.email}
                onChange={handleShareInputChange}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Phone Number</Form.Label>
              <Form.Control
                type="text"
                name="phone_number"
                value={shareForm.phone_number}
                onChange={handleShareInputChange}
              />
            </Form.Group>

            <Form.Text className="text-muted d-block mb-3">
              Email and phone number are optional individually, but at least one is required.
            </Form.Text>

            <div className="d-flex w-100 justify-content-end gap-2 align-items-stretch">
              <Button
                variant="secondary"
                className="d-inline-flex align-items-center justify-content-center text-nowrap btn-uniform-height"
                onClick={() => setShowShareModal(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="d-inline-flex align-items-center justify-content-center text-nowrap btn-uniform-height"
                disabled={sharing}
              >
                {sharing ? 'Saving...' : 'Save Share Details'}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      <Modal
        show={showImageModal}
        onHide={() => setShowImageModal(false)}
        centered
        fullscreen="md-down"
        
      >
        <Modal.Header closeButton>
        </Modal.Header>
        <Modal.Body>
          {selectedImageUrl ? (
            <div className="img-fluid mb-3">
              <InnerImageZoom
                src={selectedModalImageUrl}
                zoomSrc={selectedModalImageUrl}
                alt="Selected Product"
                zoomType="click"
                zoomPreload={false}
                zoomScale={1.5}
                zoomButton={false}

                />
              </div>
          ) : null}

          <div className="d-flex gap-2 flex-wrap">
            {selectedColorImages.length > 0 ? selectedColorImages.map((img) => {
              const thumbUrl = img.thumbnail_url || img.small_url;
              const smallOrThumbUrl = img.small_url || img.thumbnail_url || '';
              const isSelected = selectedImageUrl === smallOrThumbUrl;

              return (
                <img
                  key={`modal-${img.id}`}
                  src={thumbUrl}
                  alt={img.image_name || 'Thumbnail'}
                  onClick={() => setSelectedImageUrl(smallOrThumbUrl)}
                  style={{
                    width: '64px',
                    height: '64px',
                    objectFit: 'cover',
                    borderRadius: '6px',
                    border: isSelected ? '2px solid #0d6efd' : '1px solid #ced4da',
                    cursor: 'pointer'
                  }}
                />
              );
            }) : (
              <span className="text-muted">No thumbnails available</span>
            )}
          </div>
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default ProductDetail;
